<template>

    <v-page-body>

        <transition name="modal">
            <v-modal-domain-delete v-if="showModal === 'domain-delete'" :domain="domain" @deleted="getProject" />
            <v-modal-smtp-delete v-if="showModal === 'smtp-delete'" @deleted="getProject" />
        </transition>

        <v-page-filters floating>
            <v-tab-bar>
                <a :class="{ active: tab === 'basic' }" href="#" @click.prevent="selectTab('basic')">Basic Setup</a>
                <a :class="{ active: tab === 'advanced' }" href="#" @click.prevent="selectTab('advanced')">Advanced Setup (SMTP)</a>
            </v-tab-bar>
            <div></div>
        </v-page-filters>

        <v-page-section v-if="tab === 'basic' && domain && domain.state === 'unverified'" padded>
            <template #body>
                <v-alert type="info" message="To begin using your domain to send emails, go to your DNS provider and add the following DNS records. The MX records are not required, but are recommended for optimal delivery. When you are finished, click Verify Domain." />
            </template>
        </v-page-section>

        <v-page-section v-if="tab === 'advanced'" padded>
            <template #body>
                <v-alert type="info" message="Configure CrowdPower to send unlimited emails from your own mail server. Available for automations only." />
            </template>
        </v-page-section>

        <v-page-section v-if="tab === 'basic' && !domain" padded>
            <template #title>Domain Name</template>
            <template #body>
                <v-notice title="Add a Sending Domain" message="Add a domain name to send messages from your own domain.">
                    <template #footer>
                        <form @submit.prevent="createDomain">
                            <v-input class="w-600" size="lg" placeholder="mydomain.com" :model="params.name" @input="params.name = $event" />
                            <v-button class="m-auto mt-20" btn-type="submit" :loading="loadingCreate" @click.native="createDomain">Add Domain</v-button>
                        </form>
                    </template>
                </v-notice>
            </template>
        </v-page-section>

        <v-page-section v-if="tab === 'basic' && domain" padded>
            <template #header>
                <span>{{ domain.name_root }}</span>
                <v-identifier :uuid="domain.id" />
            </template>
            <template #body>
                <v-alert :type="domain.state === 'active' ? 'success' : 'danger'">Domain Status: {{ domain.state | capitalize }}</v-alert>
                <v-table :selectable="false" size="md" bordered>
                    <thead>
                        <th class="w-100">Status</th>
                        <th>Type</th>
                        <th>Hostname</th>
                        <th>Value</th>
                    </thead>
                    <tbody>
                        <tr v-for="record in domain.sending_dns_records" :key="record.name">
                            <td v-if="!loadingVerify" class="text-center" :class="record.valid === 'valid' ? 'text-success' : 'text-danger'">{{ record.valid === 'valid' ? 'Success' : 'Unknown' }}</td>
                            <td v-else><v-loader class="m-auto" size="sm"></v-loader></td>
                            <td>{{ record.record_type }}</td>
                            <td>{{ record.name }}</td>
                            <td><v-copy-box dark>{{ record.value }}</v-copy-box></td>
                        </tr>
                        <tr v-for="record in domain.receiving_dns_records" :key="record.name">
                            <td v-if="!loadingVerify" class="text-center" :class="record.valid === 'valid' ? 'text-success' : 'text-danger'">{{ record.valid === 'valid' ? 'Success' : 'Unknown' }}</td>
                            <td v-else><v-loader class="m-auto" size="sm"></v-loader></td>
                            <td>{{ record.record_type }}</td>
                            <td>{{ domain.name }}</td>
                            <td><v-copy-box dark>{{ record.priority }} {{ record.value }}</v-copy-box></td>
                        </tr>
                    </tbody>
                </v-table>
            </template>
            <template #footer>
                <v-button size="sm" btn-style="white-danger" @click.native="$store.commit('showModal', 'domain-delete')">Delete</v-button>
                <v-button size="sm" :loading="loadingVerify" @click.native="verifyDomain">Verify Domain</v-button>
            </template>
        </v-page-section>

        <v-page-section v-if="tab === 'advanced'">
            <template #title>SMTP Credentials</template>
            <template #body>
                <v-form-group label="Host" caption="The host address of the SMTP server.">
                    <v-input class="w-500" placeholder="smtp.mailgun.org" :model="params.host" @input="params.host = $event" />
                </v-form-group>
                <v-form-group label="Port" caption="The SMTP port (25, 465, 587, 2525).">
                    <v-input class="w-100" placeholder="587" :model="params.port" @input="params.port = $event" />
                </v-form-group>
                <v-form-group label="Username" caption="The username for the SMTP server.">
                    <v-input class="w-500" :model="params.username" @input="params.username = $event" />
                </v-form-group>
                <v-form-group label="Password" caption="The password for the SMTP server.">
                    <v-input class="w-500" :model="params.password" @input="params.password = $event" />
                </v-form-group>
            </template>
            <template #footer>
                <v-button v-if="project.smtp_config.host" size="sm" btn-style="white-danger" @click.native="$store.commit('showModal', 'smtp-delete')">Delete</v-button>
                <v-button size="sm" :loading="loadingSmtp" @click.native="updateSmtp">Save</v-button>
            </template>
        </v-page-section>

    </v-page-body>

</template>

<script>

import DomainApi from '@api/DomainApi';
import ModalDomainDelete from '@modals/misc/ModalDomainDelete';
import ProjectApi from '@api/ProjectApi';

export default {

    components: {
        'v-modal-domain-delete': ModalDomainDelete,
        'v-modal-smtp-delete': ModalDomainDelete,
    },

    data() {
        return {
            tab: 'basic',
            loadingCreate: false,
            loadingVerify: false,
            loadingSmtp: false,
            domain: null,
            params: {},
        }
    },

    mounted() {
        if (['basic', 'advanced'].includes(this.$route.params.slug)) {
            this.tab = this.$route.params.slug
        }
        this.getProject(this.tab);
    },

    watch: {

        $route(val) {
            if (['basic', 'advanced'].includes(val.params.slug)) {
                this.tab = val.params.slug;
                this.getProject(this.tab);
            }
        },

    },

    methods: {

        getProject(tab = 'basic') {
            this.$store.commit('loading', true);
            ProjectApi.getProject(this.project.id)
                .then((response) => {
                    this.$store.commit('loading', false);
                    this.domain = response.data.data.domain;
                    this.$events.$emit('pageLoaded');
                }, () => {
                    this.$store.commit('loading', false);
                    this.$events.$emit('pageError');
                });
        },

        selectTab(tab) {
            this.$router.replace({ params: { slug: tab } });
        },

        /**
         * Basic
         */

        createDomain() {
            this.loadingCreate = true;
            ProjectApi.createDomain(this.project.id, this.params)
                .then((response) => {
                    this.loadingCreate = false;
                    this.domain = response.data.data;
                }, (error) => {
                    this.loadingCreate = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        verifyDomain() {
            this.loadingVerify = true;
            DomainApi.verifyDomain(this.domain.id)
                .then((response) => {
                    this.loadingVerify = false;
                    this.domain = response.data.data;
                }, (error) => {
                    this.loadingVerify = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

        /**
         * Advanced
         */

        updateSmtp() {
            this.loadingSmtp = true;
            ProjectApi.updateSmtp(this.project.id, this.params)
                .then((response) => {
                    this.loadingSmtp = false;
                    this.$store.commit('setAlertSuccess', this.$t('message.saved'));
                }, (error) => {
                    this.loadingSmtp = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

    },

}

</script>